const KLAVIYO_LOGIN_VIA_VIPPS = "login_via_vipps_form_id"
const defaultSubscriptionListId = "T99Qz3";
const KLAVIYO_LOGIN_VIPPS_TEXT = "meld deg på med vipps"
const KLAVIYO_FORM_SELECTOR = 'form[data-testid^="klaviyo-form-"]';

/* 
read map from shopify meta fields:

custom.klaviyoformidtolistid with list of FormIdToListIdMapping

FormIdToListIdMapping
  FormId: textfield
  ListId: textfield
*/
const klaviyoFormIdToListId = {
  "WEHtp4": defaultSubscriptionListId,
  ...(window.klaviyoFormIdToListIdFromMetafields || {})
}

class KlaviyoLoginVipps {
  constructor(options) {
    options = options || {};
    this.tokens = options.tokens || {};
    // env from query params
    const queryParams = new URLSearchParams(window.location.search);
    const env = queryParams.get('env');
    this.env = env;
    this.loginButtonId = options.loginButtonId || 'vipps-login-action';
    this.sessionKey = KLAVIYO_LOGIN_VIA_VIPPS;
    this.redirectKey = 'login_via_vipps_redirect_url';
    this.customerAuth = window.theme && window.theme.customerAuth;
    this.pendingConsentKey = 'customer_auth_newsletter_intent';
    this.formId = this.customerAuth ? null : this.getFormIdFromSession();
    this.form = null;
    this.customerInfo = this.getCustomerInfo();
    this.subscriptions = new Map();

    if (this.customerAuth) {
      // The existing Vipps newsletter entry point is Norway-only.
      if (this.customerAuth.country !== 'NO') {
        this.clearPendingConsent();
        return;
      }
      this.observedForms = new WeakSet();
      this.observeOidcForms(document);
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Text and button updates are handled only by the form's own observer.
          if (mutation.target.closest && mutation.target.closest(KLAVIYO_FORM_SELECTOR)) return;
          mutation.addedNodes.forEach((node) => this.observeOidcForms(node));
        });
      }).observe(document.body, { childList: true, subtree: true });
      this.addHandlerToSubmitInKlaviyoForm();
      this.resumeOidcSubscription();
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.clearPendingConsent();
          // A restored page may belong to a customer who has since signed out.
          this.customerInfo = {};
          this.subscriptions.clear();
          document.querySelectorAll('[data-customer-auth-status]').forEach((button) => {
            delete button.dataset.customerAuthStatus;
            button.disabled = false;
            button.removeAttribute('aria-busy');
          });
          document.querySelectorAll(KLAVIYO_FORM_SELECTOR).forEach((form) => this.prepareOidcButtons(form));
        }
      });
      return;
    }
    this.addHandlerToSubmitInKlaviyoForm();
    const listId = klaviyoFormIdToListId[this.formId] || defaultSubscriptionListId;
    if (this.formId && this.customerInfo.email && listId) {
      this.subscribeToList(listId, this.customerInfo.email);
    }
  }

  getCustomerInfo() {
    const el = document.getElementById('customer-info');
    return {
      email: el?.dataset.email || null,
      id: el?.dataset.id || null,
      firstName: el?.dataset.firstName || null,
      lastName: el?.dataset.lastName || null
    }
  }

  addHandlerToSubmitInKlaviyoForm() {
    // form is dynamically added so we have to handle all clicks on button with type=button
    document.addEventListener('click', (event) => {
      const button = event.target.closest && event.target.closest('button[type="button"]');
      if (!button || button.disabled) return;
      if (this.customerAuth) {
        const form = button.closest(KLAVIYO_FORM_SELECTOR);
        if (!form) return;
        this.prepareOidcButtons(form);
        if (!button.dataset.customerAuthNewsletter || button.disabled) return;
      }
      if (button.dataset.customerAuthNewsletter || button.textContent.toLowerCase().includes(KLAVIYO_LOGIN_VIPPS_TEXT)) {
        event.preventDefault();
        event.stopPropagation();
        this.setFormIdBasedOnSubmitButton(button);
        this.handleLoginWithVippsFromKlaviyoForm();
      }
    });
  }

  observeOidcForms(root) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    const forms = root.matches && root.matches(KLAVIYO_FORM_SELECTOR)
      ? [root] : root.querySelectorAll(KLAVIYO_FORM_SELECTOR);
    forms.forEach((form) => {
      if (this.observedForms.has(form)) return;
      this.observedForms.add(form);
      this.prepareOidcButtons(form);
      new MutationObserver(() => this.prepareOidcButtons(form))
        .observe(form, { childList: true, characterData: true, subtree: true });
    });
  }

  prepareOidcButtons(form) {
    const formId = form.getAttribute('data-testid').slice('klaviyo-form-'.length);
    const signedIn = this.customerInfo.email && this.customerInfo.id;
    const mapped = this.mappedListId(formId);
    const subscription = this.subscriptions.get(formId);
    form.querySelectorAll('button[type="button"]').forEach((button) => {
      if (!button.dataset.customerAuthNewsletter && !button.textContent.toLowerCase().includes(KLAVIYO_LOGIN_VIPPS_TEXT)) return;
      button.dataset.customerAuthNewsletter = 'true';
      let label = mapped ? (signedIn ? this.customerAuth.subscribeNow : this.customerAuth.subscribe) : this.customerAuth.signIn;
      if (subscription) {
        button.dataset.customerAuthStatus = subscription.status;
        button.disabled = subscription.status !== 'retry';
        button.setAttribute('aria-live', 'polite');
        if (subscription.status === 'pending') {
          label = this.customerAuth.subscriptionPending;
          button.setAttribute('aria-busy', 'true');
        } else {
          label = subscription.status === 'sent' ? this.customerAuth.subscriptionSent : this.customerAuth.subscriptionRetry;
          button.removeAttribute('aria-busy');
        }
      }
      // Klaviyo can repaint the same button; avoid creating a mutation loop.
      this.setButtonLabel(button, label);
      if (signedIn && mapped) {
        button.removeAttribute('data-event');
      } else {
        button.setAttribute('data-event', 'customer-auth-start');
      }
    });
  }

  mappedListId(formId) {
    const value = Object.prototype.hasOwnProperty.call(klaviyoFormIdToListId, formId) && klaviyoFormIdToListId[formId];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  setButtonLabel(button, label) {
    if (button.textContent === label) return;
    // Preserve Klaviyo's child elements so its next render can reconcile them.
    const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    if (!node) return;
    node.nodeValue = label;
    while ((node = walker.nextNode())) node.nodeValue = '';
  }

  clearPendingConsent() {
    try { sessionStorage.removeItem(this.pendingConsentKey); } catch (e) { /* Storage is optional. */ }
  }

  updateOidcForm(formId) {
    document.querySelectorAll(KLAVIYO_FORM_SELECTOR).forEach((form) => {
      if (form.getAttribute('data-testid') === 'klaviyo-form-' + formId) this.prepareOidcButtons(form);
    });
  }

  async subscribeOidc(formId, listId) {
    const previous = this.subscriptions.get(formId);
    if (previous && previous.status !== 'retry') return;
    const subscription = { status: 'pending' };
    this.subscriptions.set(formId, subscription);
    this.updateOidcForm(formId);
    try {
      await this.subscribeToList(listId, this.customerInfo.email);
      subscription.status = 'sent';
    } catch (e) {
      subscription.status = 'retry';
      console.warn('Newsletter subscription failed; retry from the newsletter form.');
    } finally {
      this.updateOidcForm(formId);
    }
  }

  resumeOidcSubscription() {
    let pending;
    try {
      pending = JSON.parse(sessionStorage.getItem(this.pendingConsentKey));
      sessionStorage.removeItem(this.pendingConsentKey);
    } catch (e) {
      this.clearPendingConsent();
      return;
    }
    // Consume once, including cancelled, expired or unrelated storefront returns.
    if (!pending || !this.customerInfo.email || !this.customerInfo.id) return;
    if (typeof pending.createdAt !== 'number') return;
    const age = Date.now() - pending.createdAt;
    if (!Number.isFinite(age) || age < 0 || age > 10 * 60 * 1000) return;
    if (pending.origin !== window.location.origin || pending.path !== window.location.pathname || pending.country !== this.customerAuth.country) return;
    if (!pending.listId || this.mappedListId(pending.formId) !== pending.listId) return;
    this.subscribeOidc(pending.formId, pending.listId);
  }

  setFormIdBasedOnSubmitButton(button) {
    const form = button.closest('form');
    this.form = form;
    if (!form) {
      this.resetFormContext();
      return;
    }
    const testId = form.getAttribute('data-testid'); // klaviyo-form-WEHtp4
    if (!testId || !testId.startsWith('klaviyo-form-')) {
      this.resetFormContext();
      return;
    }

    const derivedFormId = testId.split('klaviyo-form-')[1];
    if (!derivedFormId) {
      this.resetFormContext();
      return;
    }

    this.formId = derivedFormId;
  }

  resetFormContext() {
    this.form = null;
    this.formId = null;
    this.setSessionFlag(null);
    this.clearRedirectUrl();
  }

  // 1) access / set of KLAVIYO_LOGIN_VIA_VIPPS in sessionStorage
  getFormIdFromSession() {
    try {
      return sessionStorage.getItem(this.sessionKey);
    } catch (e) {
      return null;
    }
  }

  setSessionFlag(value) {
    try {
      if (value) {
        sessionStorage.setItem(this.sessionKey, value);
      } else {
        sessionStorage.removeItem(this.sessionKey);
      }
    } catch (e) {
      // no-op
    }
  }

  getRedirectUrl() {
    try {
      return sessionStorage.getItem(this.redirectKey);
    } catch (e) {
      return null;
    }
  }

  setRedirectUrl(url) {
    try {
      if (url) {
        sessionStorage.setItem(this.redirectKey, url);
      }
    } catch (e) {
      // no-op
    }
  }

  clearRedirectUrl() {
    try {
      sessionStorage.removeItem(this.redirectKey);
    } catch (e) {
      // no-op
    }
  }

  // 2) KlaviyoLoginVippsHandle which set KLAVIYO_LOGIN_VIA_VIPPS and redirect
  handleLoginWithVippsFromKlaviyoForm() {
    if (this.customerAuth) {
      this.clearPendingConsent();
      const listId = this.mappedListId(this.formId);
      if (listId && this.customerInfo.email && this.customerInfo.id) {
        this.subscribeOidc(this.formId, listId);
        return;
      }
      if (listId) {
        try {
          sessionStorage.setItem(this.pendingConsentKey, JSON.stringify({
            formId: this.formId, listId, createdAt: Date.now(),
            origin: window.location.origin, path: window.location.pathname,
            country: this.customerAuth.country
          }));
        } catch (e) {
          // Sign-in does not depend on newsletter storage.
        }
      }
      window.location.href = this.customerAuth.loginUrl;
      return;
    }
    var _this = this;
    (function fetchVippsAuthUrl() {
      try {
        var url = 'https://g6ssodn3u8.execute-api.eu-north-1.amazonaws.com/dev/vipps/auth-url';
        if (_this.env) {
          url += ('?env=' + encodeURIComponent(_this.env));
        }
        fetch(url)
          .then(function (res) {
            if (!res.ok) {
              throw new Error('Network');
            }
            return res.json();
          })
          .then(function (data) {
            var authUrl = data && data.auth_url;
            _this.setRedirectUrl(window.location.href);
            _this.setSessionFlag(_this.formId);
            window.location.href = authUrl;
          })
          .catch(function (err) {
            console.warn('Vipps auth url fetch failed', err);
          });
      } catch (e) {
        console.warn(e);
      }
    })();
  }

  // 3) add method of subscribe to list
  async subscribeToList(listId, email) {
    var companyId = (this.tokens && (this.tokens.publicKey || this.tokens.companyId)) || 'JCKw7p';
    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId);
    var headers = { 'Content-Type': 'application/json', revision: '2025-01-15' };
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          },
          custom_source: this.customerAuth ? 'storefront_login_newsletter_form' : 'vipps_login_auto_subscribe'
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };
    try {
      var response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body)
      });
      if (response.status !== 202) {
        var err = await response.json().catch(function () { return {}; });
        throw new Error('Klaviyo subscribe failed: ' + (err && err.errors ? err.errors.map(function (e) { return e.detail; }).join(', ') : ''));
      }
      return { status: 'accepted' };
    } finally {
      if (!this.customerAuth) this.setSessionFlag(null);
    }
  }

  // 4) add method of trigger popup
  openPopup(formId) {
    var id = formId || this.formId || 'WEHtp4';
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', id]);
  }
}

window.klaviyoLoginVipps = new KlaviyoLoginVipps();
